import {optionalMaterialInput} from "#material/nodes/base/connection/validation/parameters"
import type {MaterialNodeDefinition} from "#material/nodes/base/material/types/definition"
import {ExchangeNodeName} from "#material/exchange/node-name"
import {materialNormalOutput} from "#material/nodes/base/material/validation/io"
import {materialUiSchema} from "#material/nodes/base/material/validation/ui"
import type {MaterialNodeSockets} from "#material/types/material-node-sockets"
import {vec3} from "#material/types/vector"
import {z} from "zod/v4-mini"

export interface BumpSockets extends MaterialNodeSockets {
    inputs: {
        distance: "float" // found in inputs: -; found in settings: float; inferred from data: float
        height: "float" // found in inputs: color,float,normal; found in settings: float; inferred from data: float
        normal: "normal" // found in inputs: normal,color; found in settings: vector; inferred from data: float3
        strength: "float" // found in inputs: -; found in settings: float; inferred from data: float
    }
    settings: {
        distance: "float" // found in inputs: -; found in settings: float; inferred from data: float
        height: "float" // found in inputs: color,float,normal; found in settings: float; inferred from data: float
        heightDx: "float" // found in inputs: -; found in settings: float; inferred from data: float
        heightDy: "float" // found in inputs: -; found in settings: float; inferred from data: float
        invert: "boolean" // found in inputs: -; found in settings: bool; inferred from data: boolean
        normal: "normal" // found in inputs: normal,color; found in settings: vector; inferred from data: float3
        strength: "float" // found in inputs: -; found in settings: float; inferred from data: float
    }
    outputs: {
        normal: "normal"
    }
}

export const bumpDefinition = {
    exchangeFormat: {
        nodeName: ExchangeNodeName.ShaderNodeBump,
    },
    nodeClass: "Bump",
    ui: {
        label: "Bump",
        color: "#8d802f",
        items: [
            {valueType: "normal", label: "Normal", name: "normal", output: true},
            {valueType: "float", label: "Strength", name: "strength", defaultValue: 1, input: true, setting: true},
            {valueType: "float", label: "Distance", name: "distance", defaultValue: 0.1, input: true, setting: true},
            {valueType: "float", label: "Height", name: "height", defaultValue: 1, input: true, setting: true},
            {valueType: "normal", label: "Normal", name: "normal", input: true, setting: false, defaultValue: {x: 0, y: 0, z: 0}},
            {name: "invert", label: "Invert", valueType: "boolean", defaultValue: false, input: false, setting: true},
        ],
        support: {
            cycles: true,
            three: true,
        },
    },
    validation: {
        returns: z.strictObject({normal: materialNormalOutput}),
        parameters: z.strictObject({
            inputs: z.strictObject({
                distance: optionalMaterialInput,
                height: optionalMaterialInput,
                normal: optionalMaterialInput,
                strength: optionalMaterialInput,
            }),
            settings: z.strictObject({
                distance: z.optional(z.number()),
                height: z.optional(z.number()),
                heightDx: z.optional(z.number()),
                heightDy: z.optional(z.number()),
                invert: z.optional(z.boolean()),
                normal: z.optional(vec3),
                strength: z.optional(z.number()),
            }),
            ui: materialUiSchema,
        }),
    },
} satisfies MaterialNodeDefinition<BumpSockets>
